import {
  chatCompletionsStream,
  connectOpenWebuiEvents,
  createChat,
  deleteChat,
  disconnectOpenWebuiEvents,
  getChat,
  getChatList,
  getModels,
  getGuestAiId,
  getGuestAiSession,
  getGuestAiStatus,
  getOpenwebuiToken,
  isOpenWebuiLocalMode,
  mergeChatMessages,
  removeOpenwebuiToken,
  searchChats,
  setGuestAiSession,
  setOpenwebuiToken,
  clearGuestAiSession,
  signin,
  signup,
  stopTask,
  updateChat,
} from "@/api/openWebui";
import { shouldRequestWebSearch } from "@/utils/openWebuiSearchPolicy";

const nowTimestamp = () => Math.floor(Date.now() / 1000);

export const isOpenwebuiTokenUsable = (token, now = Date.now()) => {
  try {
    const payload = String(token || "").split(".")[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return Number.isFinite(decoded.exp) && decoded.exp * 1000 > now + 30000;
  } catch (error) {
    return false;
  }
};

// Use the OpenWebUI preset, which carries the permitted base model, knowledge and tools.
const BUSINESS_MODEL_ID = "ai";
const DEFAULT_XSOU_TOOL_IDS = ["xiansou_part_number_rules"];
const OPENWEBUI_IDENTITY_KEY = "openwebui_identity_key";
const completionWaiters = new Map();
let chatMessageBatchCapability = null;
let chatSearchRequestSequence = 0;
let guestSessionSaveTimer = null;

const persistGuestSession = (state, immediate = false) => {
  const save = () => {
    guestSessionSaveTimer = null;
    setGuestAiSession({
      messages: state.messages,
      pendingQuestion: state.guestPendingQuestion,
    });
  };
  if (guestSessionSaveTimer) {
    window.clearTimeout(guestSessionSaveTimer);
    guestSessionSaveTimer = null;
  }
  if (immediate) {
    save();
  } else {
    guestSessionSaveTimer = window.setTimeout(save, 250);
  }
};

const discardGuestSession = () => {
  if (guestSessionSaveTimer) {
    window.clearTimeout(guestSessionSaveTimer);
    guestSessionSaveTimer = null;
  }
  clearGuestAiSession();
};

const hasAnsweredQuestion = (messages, question, startIndex = 0) => {
  const expected = String(question || "").trim();
  if (!expected) return true;
  const items = Array.isArray(messages) ? messages : [];
  const firstCandidateIndex = Math.max(0, Number(startIndex) || 0);
  for (
    let index = items.length - 2;
    index >= firstCandidateIndex;
    index -= 1
  ) {
    const userMessage = items[index];
    if (
      !userMessage ||
      userMessage.role !== "user" ||
      String(userMessage.content || "").trim() !== expected
    ) {
      continue;
    }
    const answer = items
      .slice(index + 1)
      .find((message) => message && message.role === "assistant");
    return Boolean(
      answer && !answer.error && String(answer.content || "").trim()
    );
  }
  return false;
};

const completionKey = (chatId, messageId) => `${chatId}:${messageId}`;

const settleCompletion = (chatId, messageId, error) => {
  const key = completionKey(chatId, messageId);
  const waiter = completionWaiters.get(key);
  if (!waiter) return;
  completionWaiters.delete(key);
  window.clearTimeout(waiter.timer);
  waiter.signal.removeEventListener("abort", waiter.abortHandler);
  if (error) {
    waiter.reject(error);
  } else {
    waiter.resolve();
  }
};

const waitForCompletion = (chatId, messageId, signal) =>
  new Promise((resolve, reject) => {
    const key = completionKey(chatId, messageId);
    const abortHandler = () => {
      const error = new Error("已停止生成");
      error.name = "AbortError";
      settleCompletion(chatId, messageId, error);
    };
    const timer = window.setTimeout(() => {
      settleCompletion(
        chatId,
        messageId,
        new Error("OpenWebUI 生成超时，请重试")
      );
    }, 10 * 60 * 1000);
    completionWaiters.set(key, {
      resolve,
      reject,
      signal,
      abortHandler,
      timer,
    });
    signal.addEventListener("abort", abortHandler, { once: true });
  });

const cancelCompletionWaiter = (chatId, messageId) => {
  const key = completionKey(chatId, messageId);
  const waiter = completionWaiters.get(key);
  if (!waiter) return;
  completionWaiters.delete(key);
  window.clearTimeout(waiter.timer);
  waiter.signal.removeEventListener("abort", waiter.abortHandler);
};

const hasCompletionWaiter = (chatId, messageId) =>
  completionWaiters.has(completionKey(chatId, messageId));

export const getOpenWebuiIdentityKey = (userInfo = {}) => {
  const mobile = String(userInfo.mobile || "").trim();
  // OpenWebUI 工具侧只能从当前账号信息里识别业务手机号，再回查 xsou-da 白名单。
  // 所以移动端账号统一优先按手机号绑定，避免 user_id 身份导致新加的料号规则管理员无法被工具识别。
  if (mobile) {
    return mobile;
  }

  const value = userInfo.user_id;
  if (value === undefined || value === null) {
    return "";
  }
  return String(value).trim();
};

const getOpenWebuiProfileImageUrl = (avatar) => {
  if (typeof avatar !== "string") {
    return "/user.png";
  }

  const value = avatar.trim();
  if (!value) {
    return "/user.png";
  }

  if (value.startsWith("data:image/")) {
    return value;
  }

  if (value === "/user.png" || value.startsWith("/user.png?")) {
    return value;
  }

  return "/user.png";
};

const buildOpenWebuiAccount = (userInfo = {}) => {
  const identityKey = getOpenWebuiIdentityKey(userInfo);
  if (!identityKey) {
    return null;
  }

  return {
    identityKey,
    email: `xsou-${identityKey}@xsou.local`,
    name:
      userInfo.nickName ||
      userInfo.nickname ||
      userInfo.nick_name ||
      userInfo.userName ||
      userInfo.username ||
      userInfo.mobile ||
      "XSOU用户",
    password: `xsou-openwebui-${identityKey}`,
    profile_image_url: getOpenWebuiProfileImageUrl(userInfo.avatar),
  };
};

const getCachedIdentityKey = () => {
  return localStorage.getItem(OPENWEBUI_IDENTITY_KEY) || "";
};

const setCachedIdentityKey = (identityKey) => {
  if (identityKey) {
    localStorage.setItem(OPENWEBUI_IDENTITY_KEY, identityKey);
  } else {
    localStorage.removeItem(OPENWEBUI_IDENTITY_KEY);
  }
};

const extractToken = (payload = {}) => {
  return (
    payload.token ||
    payload.access_token ||
    (payload.data && (payload.data.token || payload.data.access_token)) ||
    ""
  );
};

const extractUser = (payload = {}) => {
  return payload.user || payload.data || payload;
};

const isUnauthorized = (error) => {
  return error && (error.status === 401 || error.status === 403);
};

const shouldTrySignup = (error) => {
  return !error || [400, 401, 403, 404, 409].includes(error.status);
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  return error.message || fallback;
};

const getChatPermissionMessage = (error, fallback) => {
  if (isUnauthorized(error)) {
    return "当前会话属于其他账号，无法查看。请切换账号或新建会话。";
  }
  return getErrorMessage(error, fallback);
};

const normalizeModels = (payload) => {
  const rawList = Array.isArray(payload)
    ? payload
    : payload && Array.isArray(payload.data)
    ? payload.data
    : payload && Array.isArray(payload.models)
    ? payload.models
    : [];

  return rawList
    .map((model) => {
      if (typeof model === "string") {
        return { id: model, name: model };
      }
      const id = model.id || model.model || model.name;
      if (!id) return null;
      return {
        ...model,
        id,
        name: model.name || model.id || model.model || id,
      };
    })
    .filter(Boolean);
};

const normalizeChatList = (payload) => {
  const rawList = Array.isArray(payload)
    ? payload
    : payload && Array.isArray(payload.data)
    ? payload.data
    : payload && Array.isArray(payload.chats)
    ? payload.chats
    : [];

  return rawList.map((item) => ({
    ...item,
    id: item.id || item.chat_id || item.uuid,
    title: item.title || (item.chat && item.chat.title) || "新对话",
    updated_at: item.updated_at || item.updatedAt || item.created_at,
  }));
};

const getGuestMigrationKey = (chat = {}) => {
  const detail = chat.data || chat;
  const chatBody = detail.chat || {};
  const meta = chatBody.meta || chatBody.metadata || detail.meta || detail.metadata || {};
  return String(meta.guestMigrationKey || meta.guest_migration_key || "");
};

const findGuestMigrationChat = async (migrationKey) => {
  if (!migrationKey) return null;
  const chats = normalizeChatList(await getChatList(1));
  const directMatch = chats.find(
    (chat) => getGuestMigrationKey(chat) === migrationKey
  );
  if (directMatch) return normalizeChatDetail(directMatch);

  const candidates = chats.filter((chat) => chat && chat.id).slice(0, 20);
  for (let index = 0; index < candidates.length; index += 5) {
    const details = await Promise.all(
      candidates
        .slice(index, index + 5)
        .map((chat) => getChat(chat.id).catch(() => null))
    );
    const match = details.find(
      (chat) => chat && getGuestMigrationKey(chat) === migrationKey
    );
    if (match) return normalizeChatDetail(match);
  }
  return null;
};

const createMessage = ({ role, content = "", parentId = null, files = [] }) => {
  return {
    id: `${role}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    parentId,
    childrenIds: [],
    role,
    content,
    ...(files.length ? { files } : {}),
    timestamp: nowTimestamp(),
    ...(role === "assistant"
      ? { traceStartedAt: Date.now(), traceSteps: [] }
      : {}),
  };
};

const buildBatchMessage = (message = {}) => {
  const allowedFields = [
    "id",
    "parentId",
    "childrenIds",
    "role",
    "content",
    "timestamp",
    "model",
    "modelName",
    "modelIdx",
    "done",
    "error",
    "sources",
    "statusHistory",
    "output",
    "traceStartedAt",
    "traceFinishedAt",
    "traceSteps",
    "files",
  ];
  return allowedFields.reduce((result, field) => {
    if (message[field] !== undefined && message[field] !== null) {
      result[field] = message[field];
    }
    return result;
  }, {});
};

const normalizeSendPayload = (payload) => {
  if (payload && typeof payload === "object") {
    return {
      content: String(payload.content || "").trim(),
      intent: String(payload.intent || "").trim(),
      attachments: Array.isArray(payload.attachments)
        ? payload.attachments.filter(Boolean)
        : [],
    };
  }

  return {
    content: String(payload || "").trim(),
    intent: "",
    attachments: [],
  };
};

const buildChipRecognitionContext = (files = []) => {
  const imageFiles = files.filter(
    (file) => file && file.kind === "image" && file.recognition
  );
  if (!imageFiles.length) return "";

  const sections = imageFiles.map((file, fileIndex) => {
    const data = file.recognition.data || file.recognition;
    const chips = Array.isArray(data.chips) ? data.chips : [];
    const ocrLines = Array.isArray(data.ocr_lines) ? data.ocr_lines : [];
    // Older or partial recognition responses may omit this field. Treat those
    // responses as unverified instead of asserting a catalogue match.
    const mappingVerified = data.requires_confirmation === false;
    const chipLines = chips.map((chip, chipIndex) => {
      const chipOcr = Array.isArray(chip.ocr_lines)
        ? chip.ocr_lines.map((line) => line.text).filter(Boolean)
        : [];
      const edgeCounts = chip.pin_count_by_edge || {};
      return [
        `芯片${chip.index || chipIndex + 1}`,
        chip.manufacturer
          ? `${mappingVerified ? "厂商" : "厂商候选"}=${chip.manufacturer}`
          : "",
        chip.part_number
          ? `${mappingVerified ? "料号" : "料号候选"}=${chip.part_number}`
          : "",
        Number.isFinite(chip.visible_pin_count)
          ? `可见引脚=${chip.visible_pin_count}`
          : "",
        `分边引脚=上${edgeCounts.top || 0}/右${edgeCounts.right || 0}/下${
          edgeCounts.bottom || 0
        }/左${edgeCounts.left || 0}`,
        chipOcr.length ? `丝印=${chipOcr.join(" | ")}` : "",
      ]
        .filter(Boolean)
        .join("；");
    });
    const summary = data.summary || {};
    const candidates = Array.isArray(data.candidates) ? data.candidates : [];
    const topCandidate = candidates[0] || {};
    const webEnrichment = data.web_enrichment || {};
    const webSources = Array.isArray(webEnrichment.sources)
      ? webEnrichment.sources.filter((source) => source && source.url)
      : [];
    return [
      `附件${fileIndex + 1}：${file.name || "芯片图片"}`,
      `检测模式：${data.detection_mode || "unknown"}`,
      `定位芯片数：${summary.detected_count || chips.length || 0}`,
      ocrLines.length
        ? `全图OCR：${ocrLines.map((line) => line.text).filter(Boolean).join(" | ")}`
        : "",
      !mappingVerified
        ? "识别状态：候选结果，厂商和料号必须联网或人工核验后才能作为结论。"
        : "识别状态：已通过受控映射或精确目录匹配核验。",
      topCandidate.match_type
        ? `首选候选证据：类型=${topCandidate.match_type}；置信度=${topCandidate.score || 0}`
        : "",
      webEnrichment.status && webEnrichment.status !== "not_requested"
        ? `联网核验：${webEnrichment.status}`
        : "",
      ...webSources.map(
        (source) =>
          `联网来源：${source.title || source.url} | ${source.url}`
      ),
      ...chipLines,
    ]
      .filter(Boolean)
      .join("\n");
  });

  return [
    "<chip_recognition_context>",
    "以下内容来自专用芯片视觉识别服务。请结合用户问题，用千问生成简洁自然语言回答，优先给出结论，通常控制在300字以内；不要输出原始 JSON，不要推断视觉结果和可靠资料无法支持的参数。引脚数是可见引脚实验值，存在漏检时必须明确说明，不得编造。若未定位到芯片但存在全图OCR，必须优先利用OCR进行分析；只有OCR也没有有效内容时，才说明未识别到芯片或电子元件。",
    ...sections,
    "</chip_recognition_context>",
  ].join("\n");
};

const buildApiMessage = (message = {}) => {
  const content = String(message.content || "");
  if (message.role !== "user") {
    return { role: message.role, content };
  }
  const files = message.files || [];
  const recognitionContext = buildChipRecognitionContext(files);
  const attachmentPrompt = files.some((file) => file && file.kind === "image")
    ? "请识别并分析这张芯片图片。"
    : files.some((file) => file && file.kind === "pdf")
    ? "请分析这份 PDF 附件。"
    : "";
  const visiblePrompt = content.trim() || attachmentPrompt;
  return {
    role: message.role,
    content: recognitionContext
      ? `${visiblePrompt}\n\n${recognitionContext}`.trim()
      : visiblePrompt,
  };
};

const buildStatusTraceStep = (status = {}, index = 0) => {
  const action = status.action || `anonymous:${index}`;
  const traceAction =
    action === "sources_retrieved" ? "knowledge_search" : action;
  const isKnowledge =
    traceAction === "xunzhi_kb_search" || traceAction === "knowledge_search";
  const isChipWebSearch = traceAction === "chip_web_search";
  const count = Number(status.count);
  let label = status.description || status.action || "处理中";
  let detail = "";

  if (action === "xunzhi_kb_search") {
    label = !status.done
      ? "正在检索先搜知识库"
      : status.unavailable || status.error
      ? "先搜知识库服务不可用"
      : count === 0
      ? "先搜知识库未命中"
      : "先搜知识库检索完成";
    // xunzhi_kb_search.count 是后端实际返回的文档数，可以作为真实命中展示。
    detail =
      status.done &&
      !status.unavailable &&
      !status.error &&
      Number.isFinite(count) &&
      count > 0
        ? `有效命中 ${count} 条资料`
        : "";
  } else if (isKnowledge) {
    label = status.done ? "通用知识库检索完成" : "正在检索通用知识库";
    // 新检索返回真实片段数量；历史全文注入仍只能称作候选来源。
    detail =
      action === "sources_retrieved" && status.done && Number.isFinite(count)
        ? status.retrieval_mode === "snippets"
          ? count > 0
            ? `检索到 ${count} 个来源 · ${status.snippet_count || 0} 个片段`
            : "未检索到相关资料"
          : `候选来源 ${count} 个`
        : "";
  } else if (isChipWebSearch) {
    label = !status.done
      ? "正在联网检索芯片资料"
      : status.completed === false || status.error
      ? "芯片联网检索服务不可用"
      : count === 0
      ? "联网检索未发现可核验来源"
      : "芯片联网检索完成";
    detail = status.query ? `检索词：${status.query}` : "";
  } else if (status.done && !status.description) {
    label = status.action || "处理完成";
  }

  return {
    key: status.action ? `status:${traceAction}` : `status:anonymous:${index}`,
    type: isKnowledge ? "knowledge" : isChipWebSearch ? "web_search" : "status",
    label,
    detail,
    status: status.done ? "done" : "running",
  };
};

const rebuildTraceSteps = (message) => {
  if (
    message.role !== "assistant" ||
    (message.traceSteps && message.traceSteps.length)
  ) {
    return message;
  }

  const steps = [];
  const statusSteps = new Map();
  (message.statusHistory || []).forEach((status, index) => {
    if (!status) return;
    const step = buildStatusTraceStep(status, index);
    // hidden 完成事件只负责闭合先前已展示的步骤，不能单独出现在执行过程里。
    if (status.hidden === true && !statusSteps.has(step.key)) return;
    if (status.hidden === true && !step.detail) {
      step.detail = (statusSteps.get(step.key) || {}).detail || "";
    }
    statusSteps.set(step.key, step);
  });
  steps.push(
    ...[...statusSteps.values()].map((step) =>
      message.done && step.status === "running"
        ? { ...step, status: "ended" }
        : step
    )
  );

  if (message.sources && message.sources.length) {
    steps.push({
      key: "sources",
      type: "source",
      label: "已接收参考资料",
      detail: "",
      status: "done",
    });
  }

  const output = Array.isArray(message.output) ? message.output : [];
  const completedToolCalls = new Set(
    output
      .filter((item) => item && item.type === "function_call_output")
      .map((item) => item.call_id)
  );
  output
    .filter((item) => item && item.type === "function_call")
    .forEach((item) => {
      const done = completedToolCalls.has(item.call_id);
      steps.push({
        key: `tool:${item.call_id || item.id || item.name}`,
        type: "tool",
        label: done
          ? `工具 ${item.name || "调用"} 已返回`
          : `已请求工具调用 ${item.name || ""}`,
        status: done ? "done" : "ended",
      });
    });

  if (message.done || message.error) {
    steps.push({
      key: "result",
      type: "result",
      label: message.error ? "生成失败" : "执行完成",
      detail:
        message.error &&
        (message.error.content || message.error.message || message.error),
      status: message.error ? "error" : "done",
    });
  }
  if (!steps.length) return message;

  return {
    ...message,
    traceStartedAt: message.traceStartedAt || message.timestamp * 1000,
    traceFinishedAt:
      message.traceFinishedAt ||
      (message.done || message.error ? message.timestamp * 1000 : null),
    traceSteps: steps,
  };
};

const normalizeHistoryMessageFields = (message, fallbackId) => ({
  ...message,
  id: message.id || fallbackId,
  parentId: message.parentId || message.parent_id || null,
  childrenIds: message.childrenIds || message.children_ids || [],
  role: message.role,
  content: message.content || "",
  timestamp: message.timestamp || nowTimestamp(),
});

const normalizeHistoryMessage = (message, fallbackId) => {
  return rebuildTraceSteps(normalizeHistoryMessageFields(message, fallbackId));
};

const shouldKeepHistoryMessage = (message = {}) => {
  return (
    message.role !== "assistant" ||
    String(message.content || "").trim() ||
    (message.statusHistory && message.statusHistory.length) ||
    (message.traceSteps && message.traceSteps.length) ||
    message.error
  );
};

const normalizeMessages = (payload = {}) => {
  const chat = payload.chat || payload;
  const directMessages = Array.isArray(chat.messages)
    ? chat.messages
    : Array.isArray(payload.messages)
    ? payload.messages
    : null;

  if (directMessages) {
    const direct = directMessages
      .filter((message) => message && message.role)
      .map((message, index) =>
        normalizeHistoryMessageFields(message, `message-${index}`)
      );
    const historyMessages =
      chat.history && chat.history.messages ? chat.history.messages : {};
    const directIds = new Set(direct.map((message) => message.id));
    return [
      ...direct.map((message) =>
        normalizeHistoryMessage(
          {
            ...message,
            ...(historyMessages[message.id] || {}),
          },
          message.id
        )
      ),
      ...Object.keys(historyMessages)
        .filter((id) => !directIds.has(id))
        .map((id) => normalizeHistoryMessage(historyMessages[id], id)),
    ]
      .filter(shouldKeepHistoryMessage)
      .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  }

  const historyMessages =
    chat.history && chat.history.messages ? chat.history.messages : {};

  return Object.keys(historyMessages)
    .map((id) => normalizeHistoryMessage(historyMessages[id], id))
    .filter(shouldKeepHistoryMessage)
    .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
};

const normalizeChatDetail = (payload = {}) => {
  const data = payload.data || payload;
  return {
    ...data,
    id: data.id || data.chat_id || data.uuid,
    title: data.title || (data.chat && data.chat.title) || "新对话",
  };
};

const getModelToolIds = (model = {}) => {
  const meta = (model.info && model.info.meta) || model.meta || {};
  const modelToolIds = Array.isArray(meta.toolIds)
    ? meta.toolIds.filter(Boolean)
    : [];
  // 商品查询已经迁移到独立搜索页；AI 聊天只允许料号修正规则工具参与。
  // 模型 meta 里历史绑定过商品工具时必须过滤掉，否则 OpenWebUI 流式响应可能只有工具事件而没有正文。
  return Array.from(
    new Set([...modelToolIds, ...DEFAULT_XSOU_TOOL_IDS])
  ).filter((toolId) => DEFAULT_XSOU_TOOL_IDS.includes(toolId));
};

const shouldUsePartNumberRuleTool = (text = "") => {
  return /规则|修正规则|改成|修改|更改|更新|品牌改|良率/.test(text);
};

const getSelectedModelItem = (state) => {
  return (
    state.models.find((model) => model.id === state.selectedModel) || {
      id: state.selectedModel,
      name: state.selectedModel,
    }
  );
};

const buildChatModelItem = (model = {}, toolIds = []) => {
  const info = model.info ? { ...model.info } : undefined;
  const infoMeta = {
    ...((info && info.meta) || {}),
    toolIds,
  };

  // OpenWebUI 后端会参考 model_item.meta.toolIds；只清理顶层 tool_ids 不够，
  // 历史绑定的商品工具会让料号规则操作走到错误工具链并返回空响应。
  return {
    ...model,
    meta: {
      ...(model.meta || {}),
      toolIds,
    },
    ...(info
      ? {
          info: {
            ...info,
            meta: infoMeta,
          },
        }
      : {}),
  };
};

const buildHistory = (messages) => {
  const historyMessages = {};
  let previousId = null;

  messages.forEach((message) => {
    const id = message.id;
    const normalized = {
      // OpenWebUI 会把真实状态、来源和工具 output 写回消息；保存会话时必须原样带回，避免覆盖后端事件。
      ...message,
      id,
      parentId: message.parentId || previousId,
      childrenIds: [],
      role: message.role,
      content: message.content || "",
      timestamp: message.timestamp || nowTimestamp(),
    };

    if (previousId && historyMessages[previousId]) {
      historyMessages[previousId].childrenIds = [id];
    }

    historyMessages[id] = normalized;
    previousId = id;
  });

  return {
    currentId: previousId,
    messages: historyMessages,
  };
};

const buildChatPayload = ({ messages, model, currentChat }) => {
  const titleSource =
    messages.find((message) => message.role === "user" && message.content) ||
    messages[0] ||
    {};
  const existingTitle =
    currentChat &&
    (currentChat.title || (currentChat.chat && currentChat.chat.title));
  const title = String(existingTitle || titleSource.content || "新对话")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 40);
  const normalizedMessages = messages.map((message) => ({
    ...message,
    id: message.id,
    parentId: message.parentId || null,
    childrenIds: message.childrenIds || [],
    role: message.role,
    content: message.content || "",
    timestamp: message.timestamp || nowTimestamp(),
  }));

  return {
    chat: {
      ...(currentChat && currentChat.chat ? currentChat.chat : {}),
      title: title || "新对话",
      models: model ? [model] : [],
      messages: normalizedMessages,
      history: buildHistory(normalizedMessages),
      params: {},
      timestamp: nowTimestamp(),
    },
  };
};

const runWithAuthRetry = async (context, fn) => {
  try {
    return await fn();
  } catch (error) {
    if (
      isUnauthorized(error) &&
      !context.state.authRetryUsed &&
      context.state.platformUser
    ) {
      context.commit("SET_AUTH_RETRY_USED", true);
      await context.dispatch("signinOpenWebui", {
        platformUser: context.state.platformUser,
        allowSignup: true,
      });
      return await fn();
    }
    throw error;
  }
};

export default {
  namespaced: true,
  state() {
    return {
      token: getOpenwebuiToken(),
      accountIdentityKey: getCachedIdentityKey(),
      user: null,
      platformUser: null,
      models: [],
      selectedModel: "",
      chatList: [],
      chatSearchResults: [],
      chatSearchLoading: false,
      currentChat: null,
      messages: [],
      loginLoading: false,
      modelLoading: false,
      historyLoading: false,
      generating: false,
      saving: false,
      error: "",
      authRetryUsed: false,
      abortController: null,
      socketSessionId: "",
      taskId: "",
      stopRequested: false,
      guestMode: false,
      guestRemaining: 5,
      guestQuotaLoading: false,
      guestQuotaExceeded: false,
      guestPendingQuestion: "",
      guestMigrationRunning: false,
    };
  },
  mutations: {
    SET_PLATFORM_USER(state, userInfo) {
      state.platformUser = userInfo || null;
    },
    SET_AUTH(state, { token, user }) {
      state.token = token || "";
      state.user = user || null;
      if (token) {
        setOpenwebuiToken(token);
      }
    },
    CLEAR_AUTH(state) {
      disconnectOpenWebuiEvents();
      state.token = "";
      state.user = null;
      state.accountIdentityKey = "";
      removeOpenwebuiToken();
      setCachedIdentityKey("");
    },
    SET_ACCOUNT_IDENTITY_KEY(state, identityKey) {
      state.accountIdentityKey = identityKey || "";
      setCachedIdentityKey(state.accountIdentityKey);
    },
    RESET_SESSION(state, options = {}) {
      if (state.abortController) {
        state.abortController.abort();
      }
      disconnectOpenWebuiEvents();
      state.token = "";
      state.accountIdentityKey = "";
      state.user = null;
      if (!options.keepPlatformUser) {
        state.platformUser = null;
      }
      state.chatList = [];
      state.chatSearchResults = [];
      state.chatSearchLoading = false;
      chatSearchRequestSequence += 1;
      state.currentChat = null;
      state.messages = [];
      state.loginLoading = false;
      state.historyLoading = false;
      state.generating = false;
      state.saving = false;
      state.error = "";
      state.authRetryUsed = false;
      state.abortController = null;
      state.socketSessionId = "";
      state.taskId = "";
      state.stopRequested = false;
      state.guestMode = false;
      state.guestRemaining = 5;
      state.guestQuotaLoading = false;
      state.guestQuotaExceeded = false;
      state.guestPendingQuestion = "";
      state.guestMigrationRunning = false;
      removeOpenwebuiToken();
      setCachedIdentityKey("");
    },
    SET_MODELS(state, models) {
      state.models = models || [];
    },
    SET_SELECTED_MODEL(state, model) {
      state.selectedModel = model || "";
    },
    SET_CHAT_LIST(state, list) {
      state.chatList = list || [];
    },
    SET_CHAT_SEARCH_RESULTS(state, list) {
      state.chatSearchResults = list || [];
    },
    SET_CHAT_SEARCH_LOADING(state, loading) {
      state.chatSearchLoading = Boolean(loading);
    },
    UPSERT_CHAT_SUMMARY(state, chat) {
      if (!chat || !chat.id) return;
      // 轻量消息接口不返回整份会话，侧栏只需本地置顶摘要即可，无需再请求完整列表。
      state.chatList = [
        chat,
        ...state.chatList.filter((item) => item.id !== chat.id),
      ];
    },
    UPDATE_CHAT_TITLE(state, { id, title }) {
      if (!id || !title) return;
      const updateTitle = (chat) =>
        chat && chat.id === id
          ? {
              ...chat,
              title,
              ...(chat.chat
                ? { chat: { ...chat.chat, title } }
                : {}),
            }
          : chat;
      state.chatList = state.chatList.map(updateTitle);
      state.chatSearchResults = state.chatSearchResults.map(updateTitle);
      state.currentChat = updateTitle(state.currentChat);
    },
    SET_CURRENT_CHAT(state, chat) {
      state.currentChat = chat || null;
    },
    SET_MESSAGES(state, messages) {
      state.messages = messages || [];
    },
    APPEND_MESSAGE(state, message) {
      state.messages = [...state.messages, message];
    },
    UPDATE_MESSAGE_CONTENT(state, { id, content, append }) {
      state.messages = state.messages.map((message) => {
        if (message.id !== id) return message;
        return {
          ...message,
          content: append ? `${message.content || ""}${content}` : content,
        };
      });
    },
    UPSERT_MESSAGE_TRACE_STEP(state, { id, step }) {
      state.messages = state.messages.map((message) => {
        if (message.id !== id) return message;
        const now = Date.now();
        const steps = [...(message.traceSteps || [])];
        const index = steps.findIndex((item) => item.key === step.key);
        const previous = index >= 0 ? steps[index] : {};
        const next = {
          ...previous,
          ...step,
          startedAt: previous.startedAt || step.startedAt || now,
        };
        if (next.status && next.status !== "running" && !next.endedAt) {
          next.endedAt = now;
          next.durationMs = Math.max(0, now - next.startedAt);
        }
        if (index >= 0) {
          steps.splice(index, 1, next);
        } else {
          steps.push(next);
        }
        return { ...message, traceSteps: steps };
      });
    },
    FINISH_MESSAGE_TRACE(state, { id, status, label, detail }) {
      state.messages = state.messages.map((message) => {
        if (message.id !== id) return message;
        const now = Date.now();
        const steps = (message.traceSteps || []).map((step) => {
          if (step.status !== "running") return step;
          return {
            ...step,
            status: status === "done" ? "ended" : status,
            endedAt: now,
            durationMs: Math.max(0, now - step.startedAt),
          };
        });
        const result = {
          key: "result",
          type: "result",
          label,
          detail,
          status,
          startedAt: now,
          endedAt: now,
          durationMs: Math.max(0, now - (message.traceStartedAt || now)),
        };
        const resultIndex = steps.findIndex((step) => step.key === "result");
        if (resultIndex >= 0) {
          steps.splice(resultIndex, 1, result);
        } else {
          steps.push(result);
        }
        return { ...message, traceSteps: steps, traceFinishedAt: now };
      });
    },
    APPEND_MESSAGE_STATUS(state, { id, status }) {
      state.messages = state.messages.map((message) =>
        message.id === id
          ? {
              ...message,
              statusHistory: [...(message.statusHistory || []), status],
            }
          : message
      );
    },
    APPEND_MESSAGE_SOURCE(state, { id, source }) {
      state.messages = state.messages.map((message) =>
        message.id === id
          ? { ...message, sources: [...(message.sources || []), source] }
          : message
      );
    },
    MERGE_MESSAGE_EVENT_DATA(state, { id, data }) {
      state.messages = state.messages.map((message) =>
        message.id === id ? { ...message, ...data } : message
      );
    },
    REMOVE_MESSAGE(state, id) {
      state.messages = state.messages.filter((message) => message.id !== id);
    },
    SET_LOGIN_LOADING(state, loading) {
      state.loginLoading = loading;
    },
    SET_MODEL_LOADING(state, loading) {
      state.modelLoading = loading;
    },
    SET_HISTORY_LOADING(state, loading) {
      state.historyLoading = loading;
    },
    SET_GENERATING(state, loading) {
      state.generating = loading;
    },
    SET_SAVING(state, loading) {
      state.saving = loading;
    },
    SET_ERROR(state, error) {
      state.error = error || "";
    },
    SET_ABORT_CONTROLLER(state, controller) {
      state.abortController = controller || null;
    },
    SET_SOCKET_SESSION_ID(state, sessionId) {
      state.socketSessionId = sessionId || "";
    },
    SET_TASK_ID(state, taskId) {
      state.taskId = taskId || "";
    },
    SET_STOP_REQUESTED(state, requested) {
      state.stopRequested = Boolean(requested);
    },
    SET_AUTH_RETRY_USED(state, used) {
      state.authRetryUsed = used;
    },
    SET_GUEST_MODE(state, enabled) {
      state.guestMode = Boolean(enabled);
    },
    SET_GUEST_REMAINING(state, remaining) {
      const value = Number(remaining);
      state.guestRemaining = Number.isFinite(value)
        ? Math.max(0, Math.min(3, value))
        : state.guestRemaining;
    },
    SET_GUEST_QUOTA_LOADING(state, loading) {
      state.guestQuotaLoading = Boolean(loading);
    },
    SET_GUEST_QUOTA_EXCEEDED(state, exceeded) {
      state.guestQuotaExceeded = Boolean(exceeded);
    },
    SET_GUEST_PENDING_QUESTION(state, question) {
      state.guestPendingQuestion = String(question || "").slice(0, 4000);
    },
    SET_GUEST_MIGRATION_RUNNING(state, running) {
      state.guestMigrationRunning = Boolean(running);
    },
  },
  actions: {
    async bootstrap(context, platformUser) {
      context.commit("SET_GUEST_MODE", false);
      context.commit("SET_GUEST_QUOTA_EXCEEDED", false);
      context.commit("SET_PLATFORM_USER", platformUser);
      const identityKey = getOpenWebuiIdentityKey(platformUser);
      if (!identityKey) {
        context.commit("RESET_SESSION", { keepPlatformUser: true });
        context.commit("SET_ERROR", "平台用户缺少 AI 助手登录标识");
        return;
      }

      if (
        (context.state.accountIdentityKey &&
          context.state.accountIdentityKey !== identityKey) ||
        (context.state.token && !context.state.accountIdentityKey)
      ) {
        // 切换商城账号、或升级前遗留 token 没有身份绑定时，必须先清空 OpenWebUI 状态。
        // 否则新白名单手机号可能仍沿用旧账号 token，导致工具端拿不到手机号而拒绝修改规则。
        context.commit("RESET_SESSION", { keepPlatformUser: true });
      }
      context.commit("SET_ACCOUNT_IDENTITY_KEY", identityKey);
      context.commit("SET_ERROR", "");
      context.commit("SET_LOGIN_LOADING", true);

      try {
        // 本地 OpenWebUI 同样开启了鉴权，先用平台用户生成的本地账号登录拿 token，再调模型和聊天接口。
        if (!isOpenwebuiTokenUsable(context.state.token)) {
          await context.dispatch("signinOpenWebui", { platformUser, allowSignup: true });
        }
        context.commit("SET_AUTH_RETRY_USED", false);
        const modelsReady = context.state.models.length && context.state.selectedModel;
        const modelRequest = modelsReady ? Promise.resolve() : context.dispatch("loadModels");
        context.dispatch("loadChatList").catch(() => {});
        await modelRequest;
        await context.dispatch("migrateGuestSession");
      } catch (error) {
        context.commit(
          "SET_ERROR",
          getErrorMessage(error, "OpenWebUI 自动登录失败，请重试")
        );
      } finally {
        context.commit("SET_LOGIN_LOADING", false);
      }
    },

    async bootstrapGuest(context) {
      if (!context.state.guestMode) {
        context.commit("RESET_SESSION");
      }
      const guestId = getGuestAiId();
      const session = getGuestAiSession();
      context.commit("SET_GUEST_MODE", true);
      context.commit("SET_GUEST_QUOTA_EXCEEDED", false);
      context.commit("SET_ACCOUNT_IDENTITY_KEY", guestId);
      context.commit("SET_MODELS", [{ id: BUSINESS_MODEL_ID, name: BUSINESS_MODEL_ID }]);
      context.commit("SET_SELECTED_MODEL", BUSINESS_MODEL_ID);
      context.commit("SET_ERROR", "");
      if (session && session.guestId === guestId) {
        context.commit("SET_MESSAGES", session.messages);
        context.commit("SET_GUEST_PENDING_QUESTION", session.pendingQuestion);
      } else if (session) {
        discardGuestSession();
      }
      await context.dispatch("syncGuestQuota");
    },

    async syncGuestQuota(context) {
      context.commit("SET_GUEST_QUOTA_LOADING", true);
      try {
        const status = await getGuestAiStatus();
        context.commit("SET_GUEST_REMAINING", status.remainingCount);
        context.commit("SET_GUEST_QUOTA_EXCEEDED", status.remainingCount <= 0);
        return status;
      } catch (error) {
        context.commit(
          "SET_ERROR",
          getErrorMessage(error, "游客额度查询失败，请稍后重试")
        );
        return null;
      } finally {
        context.commit("SET_GUEST_QUOTA_LOADING", false);
      }
    },

    prepareGuestLogin(context, question) {
      context.commit("SET_GUEST_PENDING_QUESTION", question);
      context.commit("SET_GUEST_QUOTA_EXCEEDED", true);
      persistGuestSession(context.state, true);
    },

    clearGuestSession(context) {
      discardGuestSession();
      context.commit("SET_MESSAGES", []);
      context.commit("SET_CURRENT_CHAT", null);
      context.commit("SET_GUEST_PENDING_QUESTION", "");
      context.commit("SET_ERROR", "");
    },

    async migrateGuestSession(context) {
      if (context.state.guestMode || context.state.guestMigrationRunning) return false;
      let session = getGuestAiSession();
      if (
        !session ||
        (!session.messages.length && !String(session.pendingQuestion || "").trim())
      ) {
        return false;
      }
      if (!session.migrationKey) {
        session = setGuestAiSession({
          messages: session.messages,
          pendingQuestion: session.pendingQuestion,
        });
      }

      context.commit("SET_GUEST_MIGRATION_RUNNING", true);
      const guestMessages = normalizeMessages({ messages: session.messages });
      context.commit("SET_CURRENT_CHAT", null);
      context.commit("SET_MESSAGES", guestMessages);
      try {
        let detail = session.messages.length
          ? await findGuestMigrationChat(session.migrationKey)
          : null;
        if (!detail && session.messages.length) {
          const payload = buildChatPayload({
            messages: guestMessages,
            model: context.state.selectedModel || BUSINESS_MODEL_ID,
            currentChat: null,
          });
          payload.chat.meta = {
            ...(payload.chat.meta || {}),
            guestMigrationKey: session.migrationKey,
          };
          const response = await runWithAuthRetry(context, () => createChat(payload));
          detail = normalizeChatDetail(response);
        }
        if (detail) {
          context.commit("SET_CURRENT_CHAT", detail);
          context.commit("UPSERT_CHAT_SUMMARY", detail);
        }
        context.commit("SET_ERROR", "");

        const pendingQuestion = String(session.pendingQuestion || "").trim();
        const migratedMessages = detail ? normalizeMessages(detail) : [];
        if (
          pendingQuestion &&
          !hasAnsweredQuestion(
            migratedMessages,
            pendingQuestion,
            guestMessages.length
          )
        ) {
          await context.dispatch("sendMessage", pendingQuestion);
          if (
            !hasAnsweredQuestion(
              context.state.messages,
              pendingQuestion,
              guestMessages.length
            )
          ) {
            throw new Error("登录后自动续问失败，请点击重试");
          }
        }

        // 只有游客对话迁移完成且待发送的问题已经成功得到回答后，才删除游客草稿。
        // 登录或发送过程中刷新页面时，仍可依靠 sessionStorage 自动续接。
        discardGuestSession();
        context.commit("SET_GUEST_PENDING_QUESTION", "");
        context.dispatch("loadChatList").catch(() => {});
        return true;
      } catch (error) {
        context.commit("SET_MESSAGES", guestMessages);
        context.commit("SET_CURRENT_CHAT", null);
        context.commit(
          "SET_ERROR",
          getErrorMessage(error, "游客对话保存失败，请重试")
        );
        return false;
      } finally {
        context.commit("SET_GUEST_MIGRATION_RUNNING", false);
      }
    },

    async signinOpenWebui(context, { platformUser, allowSignup }) {
      const account = buildOpenWebuiAccount(platformUser);
      if (!account) {
        context.commit("CLEAR_AUTH");
        throw new Error("平台用户缺少 user_id，无法生成 OpenWebUI 账号");
      }

      if (
        context.state.accountIdentityKey &&
        context.state.accountIdentityKey !== account.identityKey
      ) {
        // 鉴权重试也可能发生在账号切换后，先断开旧身份再登录新身份。
        context.commit("RESET_SESSION", { keepPlatformUser: true });
      }
      context.commit("SET_ACCOUNT_IDENTITY_KEY", account.identityKey);

      const signinPayload = {
        email: account.email,
        password: account.password,
      };

      let authPayload;
      try {
        authPayload = await signin(signinPayload);
      } catch (signinError) {
        if (!allowSignup || !shouldTrySignup(signinError)) {
          throw signinError;
        }

        try {
          await signup({
            name: account.name,
            email: account.email,
            password: account.password,
            profile_image_url: account.profile_image_url,
          });
        } catch (signupError) {
          if (![400, 409].includes(signupError.status)) {
            throw signupError;
          }
        }

        authPayload = await signin(signinPayload);
      }

      const token = extractToken(authPayload);
      if (!token) {
        throw new Error("OpenWebUI 登录成功但未返回 token");
      }

      context.commit("SET_AUTH", {
        token,
        user: extractUser(authPayload),
      });
    },

    async loadModels(context) {
      context.commit("SET_MODEL_LOADING", true);
      context.commit("SET_ERROR", "");

      try {
        const payload = await runWithAuthRetry(context, () => getModels());
        const models = normalizeModels(payload);
        // 固定使用OpenWebUI维护的先搜AI模型；底座、提示词、知识库和工具统一在OpenWebUI配置。
        const businessModel = models.find(
          (model) => model.id === BUSINESS_MODEL_ID
        ) || { id: BUSINESS_MODEL_ID, name: BUSINESS_MODEL_ID };
        context.commit("SET_MODELS", [businessModel]);
        context.commit("SET_SELECTED_MODEL", BUSINESS_MODEL_ID);
      } catch (error) {
        if (isOpenWebuiLocalMode()) {
          // 本地兼容服务可能没有开放 /api/models 鉴权；兜底使用前端默认模型，让 /api/chat/completions 仍可直接调试。
          const fallbackModel = {
            id: BUSINESS_MODEL_ID,
            name: BUSINESS_MODEL_ID,
          };
          context.commit("SET_MODELS", [fallbackModel]);
          context.commit("SET_SELECTED_MODEL", fallbackModel.id);
          return;
        }
        context.commit(
          "SET_ERROR",
          isUnauthorized(error)
            ? "OpenWebUI token 已失效，请重试登录"
            : getErrorMessage(error, "模型加载失败")
        );
      } finally {
        context.commit("SET_MODEL_LOADING", false);
      }
    },

    async loadChatList(context) {
      context.commit("SET_HISTORY_LOADING", true);

      try {
        const payload = await runWithAuthRetry(context, () => getChatList(1));
        context.commit("SET_CHAT_LIST", normalizeChatList(payload));
      } catch (error) {
        context.commit("SET_ERROR", getErrorMessage(error, "会话历史加载失败"));
      } finally {
        context.commit("SET_HISTORY_LOADING", false);
      }
    },

    async searchChatList(context, { text, page = 1 } = {}) {
      const keyword = String(text || "").trim();
      if (!keyword) {
        context.dispatch("clearChatSearch");
        return [];
      }

      const requestId = ++chatSearchRequestSequence;
      context.commit("SET_CHAT_SEARCH_LOADING", true);
      context.commit("SET_CHAT_SEARCH_RESULTS", []);

      try {
        const payload = await runWithAuthRetry(context, () =>
          searchChats(keyword, page)
        );
        const results = normalizeChatList(payload);
        if (requestId === chatSearchRequestSequence) {
          context.commit("SET_CHAT_SEARCH_RESULTS", results);
          context.commit("SET_ERROR", "");
        }
        return results;
      } catch (error) {
        if (requestId === chatSearchRequestSequence) {
          context.commit(
            "SET_ERROR",
            getErrorMessage(error, "会话搜索失败，请重试")
          );
        }
        return [];
      } finally {
        if (requestId === chatSearchRequestSequence) {
          context.commit("SET_CHAT_SEARCH_LOADING", false);
        }
      }
    },

    clearChatSearch(context) {
      chatSearchRequestSequence += 1;
      context.commit("SET_CHAT_SEARCH_RESULTS", []);
      context.commit("SET_CHAT_SEARCH_LOADING", false);
    },

    async selectChat(context, chat) {
      if (context.state.generating) {
        context.commit("SET_ERROR", "请先停止当前回答，再切换会话");
        return;
      }
      const id = typeof chat === "string" ? chat : chat && chat.id;
      if (!id) return;

      context.commit("SET_HISTORY_LOADING", true);
      context.commit("SET_ERROR", "");

      try {
        const payload = await runWithAuthRetry(context, () => getChat(id));
        const detail = normalizeChatDetail(payload);
        context.commit("SET_CURRENT_CHAT", detail);
        context.commit("SET_MESSAGES", normalizeMessages(detail));
      } catch (error) {
        context.commit(
          "SET_ERROR",
          getChatPermissionMessage(error, "会话加载失败")
        );
      } finally {
        context.commit("SET_HISTORY_LOADING", false);
      }
    },

    newChat(context) {
      if (context.state.generating) {
        context.commit("SET_ERROR", "请先停止当前回答，再新建会话");
        return;
      }
      context.commit("SET_CURRENT_CHAT", null);
      context.commit("SET_MESSAGES", []);
      context.commit("SET_ERROR", "");
      context.commit("SET_GENERATING", false);
      context.commit("SET_ABORT_CONTROLLER", null);
    },

    clearCurrent(context) {
      context.dispatch("newChat");
    },

    async deleteChat(context, chat) {
      if (context.state.generating) {
        context.commit("SET_ERROR", "请先停止当前回答，再删除会话");
        return;
      }
      const id = typeof chat === "string" ? chat : chat && chat.id;
      if (!id) return;

      context.commit("SET_HISTORY_LOADING", true);
      context.commit("SET_ERROR", "");

      try {
        await runWithAuthRetry(context, () => deleteChat(id));
        if (context.state.currentChat && context.state.currentChat.id === id) {
          context.commit("SET_CURRENT_CHAT", null);
          context.commit("SET_MESSAGES", []);
        }
        await context.dispatch("loadChatList");
      } catch (error) {
        context.commit("SET_ERROR", getErrorMessage(error, "删除会话失败"));
      } finally {
        context.commit("SET_HISTORY_LOADING", false);
      }
    },

    async renameChat(context, { chat, title } = {}) {
      const id = typeof chat === "string" ? chat : chat && chat.id;
      const normalizedTitle = String(title || "")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 40);
      if (!id || !normalizedTitle) return false;

      context.commit("SET_ERROR", "");
      try {
        const payload = await runWithAuthRetry(context, () => getChat(id));
        const detail = normalizeChatDetail(payload);
        await runWithAuthRetry(context, () =>
          updateChat(id, {
            chat: {
              ...(detail.chat || {}),
              title: normalizedTitle,
            },
          })
        );
        context.commit("UPDATE_CHAT_TITLE", {
          id,
          title: normalizedTitle,
        });
        return true;
      } catch (error) {
        context.commit("SET_ERROR", getErrorMessage(error, "会话名称修改失败"));
        return false;
      }
    },

    async persistCurrentTurn(context, messages) {
      const chatId = context.state.currentChat && context.state.currentChat.id;
      // 批量接口不接受 files；带附件的轮次由调用方完整保存会话，保留附件及识别结果。
      const hasAttachments = messages.some(
        (message) => Array.isArray(message.files) && message.files.length > 0
      );
      if (
        !chatId ||
        hasAttachments ||
        isOpenWebuiLocalMode() ||
        chatMessageBatchCapability === false
      ) {
        return false;
      }

      try {
        await runWithAuthRetry(context, () =>
          mergeChatMessages(chatId, messages.map(buildBatchMessage))
        );
        chatMessageBatchCapability = true;
        const currentChat = context.state.currentChat || {};
        context.commit("UPSERT_CHAT_SUMMARY", {
          id: chatId,
          title:
            currentChat.title ||
            (currentChat.chat && currentChat.chat.title) ||
            "新对话",
          updated_at: nowTimestamp(),
        });
        return true;
      } catch (error) {
        if (error && error.status === 404) {
          // 滚动发布期间只对“接口不存在”降级，其他错误必须暴露，避免静默丢消息。
          chatMessageBatchCapability = false;
          return false;
        }
        throw error;
      }
    },

    async prepareCurrentChat(context, currentTurn) {
      if (
        context.state.currentChat &&
        context.state.currentChat.id &&
        currentTurn
      ) {
        const persisted = await context.dispatch("persistCurrentTurn", [
          currentTurn.userMessage,
          currentTurn.assistantMessage,
        ]);
        if (persisted) {
          return context.state.currentChat;
        }
      }

      const payload = buildChatPayload({
        messages: context.state.messages,
        model: context.state.selectedModel,
        currentChat: context.state.currentChat,
      });
      const response = await runWithAuthRetry(context, () => {
        if (context.state.currentChat && context.state.currentChat.id) {
          return updateChat(context.state.currentChat.id, payload);
        }
        return createChat(payload);
      });
      const detail = normalizeChatDetail(response);
      context.commit("SET_CURRENT_CHAT", detail);
      return detail;
    },

    async reconcileCurrentMessage(context, messageId) {
      const chatId = context.state.currentChat && context.state.currentChat.id;
      if (!chatId) return null;
      try {
        const payload = await runWithAuthRetry(context, () => getChat(chatId));
        const authoritative = normalizeMessages(
          normalizeChatDetail(payload)
        ).find((message) => message.id === messageId);
        if (!authoritative) return null;
        const current =
          context.state.messages.find((message) => message.id === messageId) ||
          {};
        const preferLonger = (remoteValue, localValue) =>
          String(remoteValue || "").length >= String(localValue || "").length
            ? remoteValue || ""
            : localValue || "";
        const preferLongerList = (remoteValue, localValue) => {
          const remote = Array.isArray(remoteValue) ? remoteValue : [];
          const local = Array.isArray(localValue) ? localValue : [];
          return remote.length >= local.length ? remote : local;
        };
        context.commit("MERGE_MESSAGE_EVENT_DATA", {
          id: messageId,
          data: {
            content: preferLonger(authoritative.content, current.content),
            output: preferLongerList(authoritative.output, current.output),
            statusHistory: preferLongerList(
              authoritative.statusHistory,
              current.statusHistory
            ),
            sources: preferLongerList(authoritative.sources, current.sources),
            error: authoritative.error || null,
            done: Boolean(authoritative.done || current.done),
          },
        });
        return authoritative;
      } catch (error) {
        return null;
      }
    },

    handleSocketEvent(context, event) {
      const chatId = event && event.chat_id;
      const messageId = event && event.message_id;
      const currentChatId =
        context.state.currentChat && context.state.currentChat.id;
      if (!chatId || chatId !== currentChatId || !messageId) return;

      const type = event.data && event.data.type;
      const data = (event.data && event.data.data) || {};
      const message = context.state.messages.find(
        (item) => item.id === messageId
      );
      if (!message) return;

      // 用户停止后，后端队列或 Socket 仍可能送达少量迟到事件。
      // 已标记停止的消息不再接收正文、状态和完成事件，避免界面上“停止后继续思考”。
      const messageStopped = (message.traceSteps || []).some(
        (step) => step && step.status === "stopped"
      );
      if (messageStopped && type !== "chat:tasks:cancel") return;

      if (type === "status") {
        context.commit("APPEND_MESSAGE_STATUS", {
          id: messageId,
          status: data,
        });
        const step = buildStatusTraceStep(
          data,
          (message.statusHistory || []).length
        );
        const existingStep = (message.traceSteps || []).find(
          (item) => item.key === step.key
        );
        if (data.hidden === true && !existingStep) return;
        if (data.hidden === true && !step.detail) {
          step.detail = (existingStep && existingStep.detail) || "";
        }
        context.commit("UPSERT_MESSAGE_TRACE_STEP", {
          id: messageId,
          step,
        });
        return;
      }

      if (type === "source" || type === "citation") {
        context.commit("APPEND_MESSAGE_SOURCE", {
          id: messageId,
          source: data,
        });
        const sourceCount = Array.isArray(data.document)
          ? data.document.length
          : 1;
        const previous = (message.traceSteps || []).find(
          (step) => step.key === "sources"
        );
        const count =
          (previous && previous.count ? previous.count : 0) + sourceCount;
        context.commit("UPSERT_MESSAGE_TRACE_STEP", {
          id: messageId,
          step: {
            key: "sources",
            type: "source",
            label: "已接收参考资料",
            detail: "",
            count,
            status: "done",
          },
        });
        return;
      }

      if (type === "chat:message:delta" || type === "message") {
        const content = data.content || "";
        if (content) {
          context.commit("UPDATE_MESSAGE_CONTENT", {
            id: messageId,
            content,
            append: true,
          });
          context.commit("UPSERT_MESSAGE_TRACE_STEP", {
            id: messageId,
            step: {
              key: "generation",
              type: "generation",
              label: "正在生成回答",
              status: "running",
            },
          });
        }
        return;
      }

      if (type === "chat:message" || type === "replace") {
        context.commit("UPDATE_MESSAGE_CONTENT", {
          id: messageId,
          content: data.content || "",
          append: false,
        });
        return;
      }

      if (type === "chat:message:error") {
        const detail =
          (data.error && (data.error.content || data.error.message)) ||
          "OpenWebUI 生成失败";
        context.commit("MERGE_MESSAGE_EVENT_DATA", {
          id: messageId,
          data: { error: data.error || { content: detail }, done: true },
        });
        context.commit("FINISH_MESSAGE_TRACE", {
          id: messageId,
          status: "error",
          label: "生成失败",
          detail,
        });
        settleCompletion(chatId, messageId, new Error(detail));
        return;
      }

      if (type === "chat:tasks:cancel") {
        if (message.error || message.traceFinishedAt) return;
        const error = new Error("已停止生成");
        error.name = "AbortError";
        context.commit("MERGE_MESSAGE_EVENT_DATA", {
          id: messageId,
          data: { done: true },
        });
        context.commit("FINISH_MESSAGE_TRACE", {
          id: messageId,
          status: "stopped",
          label: "已停止生成",
          detail: "",
        });
        settleCompletion(chatId, messageId, error);
        return;
      }

      if (type === "chat:active" && data.active === false) {
        if (!hasCompletionWaiter(chatId, messageId)) return;
        const error = new Error("任务已结束但未收到完成事件");
        settleCompletion(chatId, messageId, error);
        return;
      }

      if (type !== "chat:completion") return;

      context.commit("MERGE_MESSAGE_EVENT_DATA", {
        id: messageId,
        data: {
          ...(data.output ? { output: data.output } : {}),
          ...(data.usage ? { usage: data.usage } : {}),
        },
      });

      const output = Array.isArray(data.output) ? data.output : [];
      const completedToolCalls = new Set(
        output
          .filter((item) => item && item.type === "function_call_output")
          .map((item) => item.call_id)
      );
      output
        .filter((item) => item && item.type === "function_call")
        .forEach((item) => {
          const done = completedToolCalls.has(item.call_id);
          context.commit("UPSERT_MESSAGE_TRACE_STEP", {
            id: messageId,
            step: {
              key: `tool:${item.call_id || item.id || item.name}`,
              type: "tool",
              label: done
                ? `工具 ${item.name || "调用"} 已返回`
                : `已请求工具调用 ${item.name || ""}`,
              status: done ? "done" : "running",
            },
          });
        });

      const choice = Array.isArray(data.choices) ? data.choices[0] || {} : {};
      const choiceContent =
        (choice.message && choice.message.content) ||
        (choice.delta && choice.delta.content) ||
        "";
      if (choiceContent) {
        context.commit("UPDATE_MESSAGE_CONTENT", {
          id: messageId,
          content: choiceContent,
          append: !data.done,
        });
      }
      if (typeof data.content === "string" && data.content) {
        context.commit("UPDATE_MESSAGE_CONTENT", {
          id: messageId,
          content: data.content,
          append: false,
        });
      }
      if (choiceContent || data.content) {
        context.commit("UPSERT_MESSAGE_TRACE_STEP", {
          id: messageId,
          step: {
            key: "generation",
            type: "generation",
            label: data.done ? "回答生成完成" : "正在生成回答",
            status: data.done ? "done" : "running",
          },
        });
      }

      if (data.done) {
        context.commit("MERGE_MESSAGE_EVENT_DATA", {
          id: messageId,
          data: { done: true },
        });
        const elapsed = Math.max(
          0,
          Date.now() - (message.traceStartedAt || Date.now())
        );
        context.commit("FINISH_MESSAGE_TRACE", {
          id: messageId,
          status: "done",
          label: "执行完成",
          detail: `用时 ${(elapsed / 1000).toFixed(1)} 秒`,
        });
        settleCompletion(chatId, messageId);
      }
    },

    async sendSearchMessage(context, text) {
      const identity = context.state.accountIdentityKey;
      const model = context.state.selectedModel;
      const userMessage = createMessage({ role: "user", content: text });
      const assistantMessage = createMessage({ role: "assistant", parentId: userMessage.id });
      userMessage.childrenIds = [assistantMessage.id];
      context.commit("APPEND_MESSAGE", userMessage);
      context.commit("APPEND_MESSAGE", assistantMessage);
      context.commit("SET_ERROR", "");
      context.commit("SET_GENERATING", true);
      context.commit("SET_STOP_REQUESTED", false);
      const controller = new AbortController();
      context.commit("SET_ABORT_CONTROLLER", controller);
      const isCurrent = () => context.state.accountIdentityKey === identity &&
        context.state.messages.some(message => message.id === assistantMessage.id);
      try {
        await runWithAuthRetry(context, () => chatCompletionsStream({
          token: context.state.token,
          signal: controller.signal,
          payload: {
            model, messages: [{ role: "user", content: text }], stream: true,
            features: { fast_parse: true, ...(shouldRequestWebSearch({ text }) ? { web_search: true } : {}) },
          },
          onToken: content => {
            if (isCurrent() && !controller.signal.aborted) context.commit("UPDATE_MESSAGE_CONTENT", {
              id: assistantMessage.id, content, append: true,
            });
          },
          onReplace: content => {
            if (isCurrent() && !controller.signal.aborted) context.commit("UPDATE_MESSAGE_CONTENT", {
              id: assistantMessage.id, content, append: false,
            });
          },
        }));
        if (!isCurrent() || controller.signal.aborted) return;
        context.commit("MERGE_MESSAGE_EVENT_DATA", { id: assistantMessage.id, data: { done: true } });
        context.commit("FINISH_MESSAGE_TRACE", { id: assistantMessage.id, status: "done", label: "执行完成" });
        const completedMessage = context.state.messages.find(message => message.id === assistantMessage.id);
        const snapshot = buildChatPayload({ messages: [userMessage, { ...completedMessage }], model });
        // Capture the account token and completed turn; saving must not change
        // the answer or overwrite a newer search when the request finishes.
        createChat(snapshot, context.state.token).then(response => {
          if (context.state.accountIdentityKey !== identity) return;
          const detail = normalizeChatDetail(response);
          context.commit("UPSERT_CHAT_SUMMARY", { id: detail.id, title: text, updated_at: nowTimestamp() });
          if (context.state.messages.at(-1)?.id === assistantMessage.id) context.commit("SET_CURRENT_CHAT", detail);
        }).catch(error => {
          if (isCurrent()) context.commit("SET_ERROR", getErrorMessage(error, "回答已生成，但会话保存失败"));
        });
      } catch (error) {
        if (isCurrent() && !controller.signal.aborted) context.commit("SET_ERROR", getErrorMessage(error, "搜索回答生成失败"));
      } finally {
        if (context.state.abortController === controller) {
          context.commit("SET_GENERATING", false);
          context.commit("SET_ABORT_CONTROLLER", null);
        }
      }
    },

    async sendMessage(context, payload) {
      const { content: text, attachments, intent } = normalizeSendPayload(payload);
      if ((!text && !attachments.length) || context.state.generating) return;

      if (context.state.guestMode) {
        if (attachments.length) {
          context.commit("SET_ERROR", "登录后可使用图片和文件识别功能");
          return { loginRequired: true };
        }
        return context.dispatch("sendGuestMessage", text);
      }

      if (!context.state.selectedModel) {
        context.commit("SET_ERROR", "无可用模型，无法发送消息");
        return;
      }

      if (intent === "search-preview" && !attachments.length) {
        return context.dispatch("sendSearchMessage", text);
      }

      const parent = [...context.state.messages]
        .reverse()
        .find(
          (message) =>
            message.role !== "assistant" || String(message.content || "").trim()
        );
      const userMessage = createMessage({
        role: "user",
        content: text,
        parentId: parent ? parent.id : null,
        files: attachments,
      });
      const assistantMessage = createMessage({
        role: "assistant",
        content: "",
        parentId: userMessage.id,
      });
      userMessage.childrenIds = [assistantMessage.id];

      context.commit("APPEND_MESSAGE", userMessage);
      context.commit("APPEND_MESSAGE", assistantMessage);
      context.commit("SET_GENERATING", true);
      context.commit("SET_STOP_REQUESTED", false);
      context.commit("SET_ERROR", "");

      const controller = new AbortController();
      context.commit("SET_ABORT_CONTROLLER", controller);
      let chatId = "";
      let completionPromise = null;

      try {
        if (isOpenWebuiLocalMode()) {
          disconnectOpenWebuiEvents();
          context.commit("SET_SOCKET_SESSION_ID", "");
        } else {
          await connectOpenWebuiEvents({
            token: context.state.token,
            onEvent: (event) => context.dispatch("handleSocketEvent", event),
            onSession: (id) => context.commit("SET_SOCKET_SESSION_ID", id),
          });
        }
        // OpenWebUI 只会把真实 status/source/tool 事件关联到已存在的消息，
        // 因此新旧会话都先保存本轮 assistant 占位，再提交后台生成任务。
        const currentChat = await context.dispatch("prepareCurrentChat", {
          userMessage,
          assistantMessage,
        });
        chatId = currentChat.id;
        const apiMessages = context.state.messages
          .filter(
            (message) =>
              message.id !== assistantMessage.id &&
              (message.role !== "assistant" ||
                String(message.content || "").trim())
          )
          .map(buildApiMessage);
        const selectedModelItem = getSelectedModelItem(context.state);
        const hasChipImageAttachments = attachments.some(
          (file) => file && file.kind === "image" && file.recognition
        );
        const requestWebSearch = shouldRequestWebSearch({
          localMode: isOpenWebuiLocalMode(),
          hasChipImageAttachments,
          text,
        });
        const toolIds = shouldUsePartNumberRuleTool(text)
          ? getModelToolIds(selectedModelItem)
          : [];
        const chatModelItem = buildChatModelItem(selectedModelItem, toolIds);

        completionPromise = waitForCompletion(
          chatId,
          assistantMessage.id,
          controller.signal
        );
        const task = await runWithAuthRetry(context, async () => {
          const sessionId = isOpenWebuiLocalMode()
            ? ""
            : await connectOpenWebuiEvents({
                token: context.state.token,
                onEvent: (event) => context.dispatch("handleSocketEvent", event),
                onSession: (id) =>
                  context.commit("SET_SOCKET_SESSION_ID", id),
              });
          context.commit("SET_SOCKET_SESSION_ID", sessionId);
          return chatCompletionsStream({
            token: context.state.token,
            signal: controller.signal,
            payload: {
              model: context.state.selectedModel,
              messages: apiMessages,
              stream: true,
              // 规则和百科未命中的纯料号继续联网检索；本地聊天和芯片图片保持原有检索能力。
              features: intent === "wiki-preview"
                ? { wiki_preview: true }
                : requestWebSearch
                ? { web_search: true }
                : undefined,
              // 只保留料号规则工具能力；商城商品查询已经迁移到独立搜索页，避免 AI completions 偶发返回空响应。
              tool_ids: toolIds.length ? toolIds : undefined,
              model_item: chatModelItem,
              chat_id: chatId,
              id: assistantMessage.id,
              parent_id: userMessage.id,
              parent_message: userMessage,
              // Keep image metadata as well as PDF metadata in the request. The
              // backend uses it to bypass the material-number parser and route a
              // chip photo to Qwen's multimodal answer path.
              files: attachments
                .filter((file) => (file.kind === "pdf" || file.kind === "image") && file.id)
                .map((file) => ({
                  id: file.id,
                  name: file.name,
                  type: file.kind === "image" ? "image" : "file",
                  content_type:
                    file.content_type ||
                    (file.kind === "image" ? "image/*" : "application/pdf"),
                })),
              session_id: sessionId,
            },
            onToken: (tokenText) => {
              if (context.state.stopRequested) return;
              context.commit("UPDATE_MESSAGE_CONTENT", {
                id: assistantMessage.id,
                content: tokenText,
                append: true,
              });
              context.commit("UPSERT_MESSAGE_TRACE_STEP", {
                id: assistantMessage.id,
                step: {
                  key: "generation",
                  type: "generation",
                  label: "正在生成回答",
                  status: "running",
                },
              });
            },
            onReplace: (contentText) => {
              if (context.state.stopRequested) return;
              context.commit("UPDATE_MESSAGE_CONTENT", {
                id: assistantMessage.id,
                content: contentText,
                append: false,
              });
            },
            onEvent: (event) =>
              context.dispatch("handleSocketEvent", {
                chat_id: chatId,
                message_id: assistantMessage.id,
                data: event.data || event,
              }),
          });
        });
        context.commit("SET_TASK_ID", task && task.task_id);
        // 兼容普通 JSON/SSE：没有 task_id 说明 HTTP 请求本身已经完成，无需继续等 Socket 事件。
        if (!task || !task.task_id) {
          context.commit("MERGE_MESSAGE_EVENT_DATA", {
            id: assistantMessage.id,
            data: { done: true },
          });
          context.commit("FINISH_MESSAGE_TRACE", {
            id: assistantMessage.id,
            status: "done",
            label: "执行完成",
          });
          settleCompletion(chatId, assistantMessage.id);
        }
        if (context.state.stopRequested) {
          await context.dispatch("stopGeneration");
        }
        await completionPromise;

        const assistant = context.state.messages.find(
          (message) => message.id === assistantMessage.id
        );
        if (assistant && assistant.content) {
          // 正常完成时 Socket 已提供权威正文和事件，直接合并本轮，避免再下载整份大会话。
          const currentUser = context.state.messages.find(
            (message) => message.id === userMessage.id
          );
          const currentAssistant = context.state.messages.find(
            (message) => message.id === assistantMessage.id
          );
          const persisted = await context.dispatch("persistCurrentTurn", [
            currentUser,
            currentAssistant,
          ]);
          if (!persisted) {
            await context.dispatch("saveCurrentChat");
          }
        } else {
          const authoritative = await context.dispatch(
            "reconcileCurrentMessage",
            assistantMessage.id
          );
          if (
            authoritative &&
            authoritative.done === true &&
            String(authoritative.content || "").trim() &&
            !authoritative.error
          ) {
            context.commit("FINISH_MESSAGE_TRACE", {
              id: assistantMessage.id,
              status: "done",
              label: "执行完成",
              detail: "已从服务端恢复结果",
            });
            const currentUser = context.state.messages.find(
              (message) => message.id === userMessage.id
            );
            const currentAssistant = context.state.messages.find(
              (message) => message.id === assistantMessage.id
            );
            const persisted = await context.dispatch("persistCurrentTurn", [
              currentUser,
              currentAssistant,
            ]);
            if (!persisted) {
              await context.dispatch("saveCurrentChat");
            }
            return;
          }
          const generationError = (authoritative && authoritative.error) || (assistant && assistant.error);
          const failureMessage = (typeof generationError === "string" ? generationError : generationError && (generationError.content || generationError.message)) || "OpenWebUI 返回空响应";
          context.commit("MERGE_MESSAGE_EVENT_DATA", {
            id: assistantMessage.id,
            data: {
              error: { content: failureMessage },
              done: true,
            },
          });
          context.commit("FINISH_MESSAGE_TRACE", {
            id: assistantMessage.id,
            status: "error",
            label: "生成失败",
            detail: failureMessage,
          });
          context.commit("SET_ERROR", failureMessage);
        }
      } catch (error) {
        if (chatId) {
          cancelCompletionWaiter(chatId, assistantMessage.id);
        }
        // Socket 断开或超时时，先用服务端记录对账；已落库的成功结果优先于本地临时错误。
        const authoritative = await context.dispatch(
          "reconcileCurrentMessage",
          assistantMessage.id
        );
        const recovered =
          authoritative &&
          error &&
          error.name !== "AbortError" &&
          !context.state.stopRequested &&
          authoritative.done === true &&
          String(authoritative.content || "").trim() &&
          !authoritative.error;
        if (recovered) {
          context.commit("FINISH_MESSAGE_TRACE", {
            id: assistantMessage.id,
            status: "done",
            label: "执行完成",
            detail: "已从服务端恢复结果",
          });
          context.commit("SET_ERROR", "");
          return;
        }
        const assistant = context.state.messages.find(
          (message) => message.id === assistantMessage.id
        );

        if (error && error.name === "AbortError") {
          context.commit("MERGE_MESSAGE_EVENT_DATA", {
            id: assistantMessage.id,
            data: { done: true },
          });
          if (assistant && !assistant.traceFinishedAt) {
            context.commit("FINISH_MESSAGE_TRACE", {
              id: assistantMessage.id,
              status: "stopped",
              label: "已停止生成",
              detail: "",
            });
          }
        } else {
          context.commit("MERGE_MESSAGE_EVENT_DATA", {
            id: assistantMessage.id,
            data: {
              error: {
                content: getErrorMessage(error, "消息发送失败，请重试"),
              },
              done: true,
            },
          });
          if (assistant && !assistant.traceFinishedAt) {
            context.commit("FINISH_MESSAGE_TRACE", {
              id: assistantMessage.id,
              status: "error",
              label: "生成失败",
              detail: getErrorMessage(error, "消息发送失败，请重试"),
            });
          }
          context.commit(
            "SET_ERROR",
            getErrorMessage(error, "消息发送失败，请重试")
          );
        }
      } finally {
        context.commit("SET_GENERATING", false);
        context.commit("SET_ABORT_CONTROLLER", null);
        context.commit("SET_TASK_ID", "");
        context.commit("SET_STOP_REQUESTED", false);
      }
    },

    async sendGuestMessage(context, text) {
      if (context.state.guestRemaining <= 0) {
        context.dispatch("prepareGuestLogin", text);
        return { loginRequired: true };
      }

      const parent = [...context.state.messages]
        .reverse()
        .find(
          (message) =>
            message.role !== "assistant" || String(message.content || "").trim()
        );
      const userMessage = createMessage({
        role: "user",
        content: text,
        parentId: parent ? parent.id : null,
      });
      const assistantMessage = createMessage({
        role: "assistant",
        content: "",
        parentId: userMessage.id,
      });
      userMessage.childrenIds = [assistantMessage.id];
      context.commit("APPEND_MESSAGE", userMessage);
      context.commit("APPEND_MESSAGE", assistantMessage);
      persistGuestSession(context.state, true);
      context.commit("SET_GENERATING", true);
      context.commit("SET_GUEST_QUOTA_EXCEEDED", false);
      context.commit("SET_ERROR", "");

      const controller = new AbortController();
      context.commit("SET_ABORT_CONTROLLER", controller);
      try {
        const messages = context.state.messages
          .filter(
            (message) =>
              message.id !== assistantMessage.id &&
              ["user", "assistant"].includes(message.role) &&
              String(message.content || "").trim()
          )
          .slice(-20)
          .map((message) => ({
            role: message.role,
            content: String(message.content || "").slice(0, 4000),
          }));
        await chatCompletionsStream({
          url: "/dev-api/api/mts-api/guest-ai/chat",
          includeOpenwebuiAuth: false,
          validateBusinessResponse: true,
          requireContent: true,
          signal: controller.signal,
          payload: {
            guestId: getGuestAiId(),
            messages,
          },
          onResponse: (response) => {
            const remaining = response.headers.get("X-Guest-Ai-Remaining");
            if (remaining !== null) {
              context.commit("SET_GUEST_REMAINING", remaining);
            }
          },
          onToken: (content) => {
            if (controller.signal.aborted) return;
            context.commit("UPDATE_MESSAGE_CONTENT", {
              id: assistantMessage.id,
              content,
              append: true,
            });
            persistGuestSession(context.state);
          },
          onReplace: (content) => {
            if (controller.signal.aborted) return;
            context.commit("UPDATE_MESSAGE_CONTENT", {
              id: assistantMessage.id,
              content,
              append: false,
            });
            persistGuestSession(context.state);
          },
        });
        context.commit("MERGE_MESSAGE_EVENT_DATA", {
          id: assistantMessage.id,
          data: { done: true },
        });
        persistGuestSession(context.state, true);
        await context.dispatch("syncGuestQuota");
        return { remaining: context.state.guestRemaining };
      } catch (error) {
        const quotaExceeded =
          error.status === 429 ||
          (error.data && error.data.code === "GUEST_QUOTA_EXHAUSTED");
        if (quotaExceeded) {
          context.commit("SET_GUEST_REMAINING", 0);
          context.commit("SET_GUEST_QUOTA_EXCEEDED", true);
          context.commit("SET_GUEST_PENDING_QUESTION", text);
          context.commit("REMOVE_MESSAGE", userMessage.id);
          context.commit("REMOVE_MESSAGE", assistantMessage.id);
          persistGuestSession(context.state, true);
          return { loginRequired: true };
        }
        if (!controller.signal.aborted) {
          context.commit(
            "SET_ERROR",
            getErrorMessage(error, "游客问答失败，请稍后重试")
          );
        }
        persistGuestSession(context.state, true);
        await context.dispatch("syncGuestQuota");
        return { error };
      } finally {
        if (context.state.abortController === controller) {
          context.commit("SET_GENERATING", false);
          context.commit("SET_ABORT_CONTROLLER", null);
        }
      }
    },

    async stopGeneration(context) {
      if (!context.state.generating) return;

      context.commit("SET_STOP_REQUESTED", true);
      const taskId = context.state.taskId;
      const controller = context.state.abortController;
      const assistant = [...context.state.messages]
        .reverse()
        .find((message) => message.role === "assistant" && !message.done);

      // 先在本地完成停止状态，让按钮点击后立即终止流式读取和页面思考态。
      if (assistant) {
        context.commit("MERGE_MESSAGE_EVENT_DATA", {
          id: assistant.id,
          data: { done: true },
        });
        if (!assistant.traceFinishedAt) {
          context.commit("FINISH_MESSAGE_TRACE", {
            id: assistant.id,
            status: "stopped",
            label: "已停止生成",
            detail: "",
          });
        }
      }
      if (controller && !controller.signal.aborted) {
        controller.abort();
      }

      // 后台任务模式下再通知 OpenWebUI 停止服务端任务；普通 SSE 模式由上面的 abort 中断。
      if (!taskId) return;
      try {
        await runWithAuthRetry(context, () => stopTask(taskId));
      } catch (error) {
        context.commit("SET_ERROR", "已停止当前回复，但服务端任务停止失败");
      }
    },

    async saveCurrentChat(context) {
      if (!context.state.messages.length) return;

      context.commit("SET_SAVING", true);

      try {
        const payload = buildChatPayload({
          messages: context.state.messages,
          model: context.state.selectedModel,
          currentChat: context.state.currentChat,
        });

        if (isOpenWebuiLocalMode()) {
          let currentChat = context.state.currentChat;
          if (!currentChat || !currentChat.id) {
            const response = await createChat(payload);
            currentChat = normalizeChatDetail(response);
            context.commit("SET_CURRENT_CHAT", currentChat);
          }

          // 本地 OpenWebUI 和线上一样通过聊天详情接口保存会话内容，避免误调用完成回调造成 400。
          const response = await updateChat(currentChat.id, payload);
          const detail = normalizeChatDetail(response);
          context.commit("SET_CURRENT_CHAT", detail);
          // 本地后端保存前会用全局料号规则做最终修正，保存后同步 messages，避免流式原文继续停留在当前屏幕。
          context.commit("SET_MESSAGES", normalizeMessages(detail));
          context.commit("SET_ERROR", "");
          context.dispatch("loadChatList").catch(() => {});
          return;
        }

        const response = await runWithAuthRetry(context, () => {
          if (context.state.currentChat && context.state.currentChat.id) {
            return updateChat(context.state.currentChat.id, payload);
          }
          return createChat(payload);
        });

        context.commit("SET_CURRENT_CHAT", normalizeChatDetail(response));
        context.dispatch("loadChatList").catch(() => {});
      } catch (error) {
        context.commit("SET_ERROR", getErrorMessage(error, "会话保存失败"));
      } finally {
        context.commit("SET_SAVING", false);
      }
    },

    setSelectedModel(context) {
      if (context.state.generating) {
        context.commit("SET_ERROR", "当前正在生成，完成后再切换模型");
        return;
      }
      context.commit("SET_SELECTED_MODEL", BUSINESS_MODEL_ID);
    },

    resetSession(context) {
      context.commit("RESET_SESSION");
    },
  },
};
